<template>
  <div class="ticket-create-new-ticket__container">
    <organism-ticket-detail
      v-if="!isLoading"
      :ticketId="id"
      :type="type"
      :status="status"
      :customerData="customerData"
      :ticketData="ticketData"
      :ticketDetailData="ticketDetailData"
      :departmentList="departmentList"
      :priorityList="priorityList"
      :sourceList="sourceList"
      :topicList="topicList"
      :staffList="staffList"
      :ticketTimestamp="ticketTimestamp"
      :internalThreadList="internalThreadList"
      :activityCommunityList="activityCommunityList"
      :replyTemplateOptions="replyTemplateOptions"
      :escalationTemplateList="escalationTemplateList"
      :escalation="escalation"
      :escalationDetail="escalationDetail"
      :isNoEscalationSelected="isNoEscalationSelected"
      @changeEscalationTemplate="changeEscalationTemplate"
      @onClickEscalateLevel="onClickEscalateLevel"
      @onClickDownloadFile="onClickDownloadFile"
      @onClickSaveEditTicket="onClickSaveEditTicket"
      @onClickAddTicketInternal="onClickAddTicketInternal"
      @onClickAddTicketActivity="onClickAddTicketActivity"
      @onRequestDeleteAttachment="onRequestDeleteAttachment"
      @onRequestDeleteAttachmentFromThread="onRequestDeleteAttachmentFromThread"
    ></organism-ticket-detail>
  </div>
</template>

<script>
import { DateTime } from "luxon"
import {
  getTopicList,
  getTicketById,
  getThreadByTicketId,
  updateTicketById,
  uploadTicketAttachments,
  deleteTicketAttachment,
  addThreadByTicketId,
  uploadThreadAttachments,
  replyTemplates,
  deleteThreadAttachment,
  getAllUserList,
} from "@/api/api-module1"
import mixins from "@/mixins"
import OrganismTicketDetail from "@/components/organisms/portal/internal/ticket/OrganismTicketDetail.vue"
import mixinsSiteHelper from "@/mixins/mixinsSiteHelper"
import { getDepartmentListBySiteId } from "@/api/api-module-data"
import {
  escalateTicketEscalationByTicketId,
  getTicketEscalationActiveList,
  getTicketEscalationById,
  getTicketEscalationByTicketId,
} from "@/api/module-1/escalation"

export default {
  name: "TicketDetail",
  components: { OrganismTicketDetail },
  mixins: [mixins, mixinsSiteHelper],
  data() {
    return {
      // id
      id: "",

      // status
      status: "",

      // customer
      customerData: null,

      // ticket
      ticketData: null,
      topicList: null,
      type: null,

      // ticketDetail
      ticketDetailData: null,
      departmentList: null,
      priorityList: [
        { id: "CRITICAL", name: "Critical" },
        { id: "HIGH", name: "High" },
        { id: "MEDIUM", name: "Medium" },
        { id: "LOW", name: "Low" },
      ],
      sourceList: [
        { id: "TICKET", name: "Ticket" },
        { id: "PHONE", name: "Phone" },
        { id: "EMAIL", name: "Email" },
        { id: "OTHER", name: "Other" },
      ],

      // staff
      staffList: null,

      // ticket detail
      ticketTimestamp: null,
      internalThreadList: null,
      activityCommunityList: null,

      // reply template options
      replyTemplateOptions: null,
      escalationTemplateList: null,
      escalation: null,
      escalationDetail: {
        name: "",
        description: "",
        levels: [],
      },
      isNoEscalationSelected: false,
    }
  },
  computed: {
    isLoading() {
      return (
        !this.topicList ||
        !this.customerData ||
        !this.ticketData ||
        !this.ticketDetailData ||
        !this.ticketTimestamp ||
        !this.departmentList ||
        !this.internalThreadList ||
        !this.activityCommunityList ||
        !this.staffList ||
        !this.replyTemplateOptions
      )
    },
    ticketId() {
      return this.$route?.params?.ticketId.toString()
    },
  },
  watch: {
    ticketId: {
      deep: true,
      immediate: true,
      handler() {
        this.id = this.ticketId
        this.getTicketEscalationActiveList()
        this.getListDataFromAPI()
        this.getTicketDataFromAPI()
      },
    },
    isLoading: {
      deep: true,
      immediate: true,
      handler() {
        this.isLoading ? this.showLoading(true) : this.showLoading(false)
      },
    },
  },
  methods: {
    // get data
    async getListDataFromAPI() {
      await Promise.all([
        this.getTopicListFromApi(),
        this.replyTemplateOptionsFromApi(),
      ])
    },

    async getTopicListFromApi() {
      // ticket
      await getTopicList().then((response) => {
        this.topicList = response.map((topic) => {
          return {
            id: topic.topicId,
            name: topic.topicName,
          }
        })
      })
    },

    async getDepartmentListFromApi(siteId) {
      await getDepartmentListBySiteId(siteId).then((response) => {
        this.departmentList = response.map((department) => {
          return {
            id: department.departmentId,
            name: department.departmentName,
            isSupportDepartment: department.isSupportDepartment,
          }
        })
      })
    },

    async getStaffListFromApi(siteId) {
      const filter = { siteIds: [siteId] }
      await getAllUserList(filter).then((response) => {
        this.staffList = response.map((user) => ({
          id: user.username,
          name: `${user.firstName} ${user.lastName}`,
        }))
      })
    },

    async replyTemplateOptionsFromApi() {
      // reply templated
      await replyTemplates().then((response) => {
        this.replyTemplateOptions = response.map((template) => {
          return {
            id: template.replyTemplateId,
            name: template.replyTemplateName,
            message: template.replyTemplateMessage,
          }
        })
      })
    },

    async getTicketDataFromAPI() {
      await Promise.all([
        this.getTicketByIdFromApi(),
        this.getThreadsFromApi(),
        this.getActivitiesFromApi(),
      ])
    },

    async getTicketByIdFromApi() {
      await getTicketById(this.id)
        .then(async (ticket) => {
          this.type = ticket?.ticketType
          // status
          this.status = ticket?.status

          const siteName = ticket?.siteId
            ? await this.getSingleSiteNameBySiteId(ticket.siteId)
            : null

          // customer
          this.customerData = {
            name: ticket?.contractCustomerName,
            email: ticket?.contractEmails?.split(",")?.join(", "),
            phone: ticket?.contractPhones,
            siteId: ticket?.siteId,
            siteName,
            idcId: ticket?.contractId
              ? ticket?.contractId?.toString()?.padStart(8, "0")
              : null,
            ticketType: ticket?.ticketType,
            ticketId: ticket?.ticketId,
          }

          this.isNoEscalationSelected = !ticket?.escalationId

          this.escalation = this.escalationTemplateList?.find(
            (escalation) => escalation?.escalationId === ticket?.escalationId
          )

          if (ticket?.escalationId) {
            await this.getTicketEscalationDetailByTicketId(this.id)
          }

          // ticket
          this.ticketData = {
            topic: this.topicList?.find((t) => t?.id === ticket?.topicId),
            subject: ticket?.ticketSubject,
            detail: ticket?.ticketDetail,
            attachments: ticket?.attachments,
            priority: this.priorityList?.find(
              (p) => p?.id === ticket?.priority
            ),
          }

          const department = await this.getDepartmentOptionByDepartmentId(
            ticket.responsibleDepartmentId
          )

          // ticketDetail
          this.ticketDetailData = {
            department: department
              ? {
                  id: department?.departmentId,
                  name: department?.departmentName,
                }
              : null,
            source: this.sourceList?.find((s) => s?.id === ticket?.source),
            createdBy: ticket?.openedBy,
            updatedBy: ticket?.updatedBy,
            escalationId: ticket?.escalationId,
          }

          // ticket detail
          this.ticketTimestamp = {
            createdAt: DateTime?.fromISO(ticket.openedAt)?.toFormat(
              "dd/MM/yyyy HH:mm:ss"
            ),
            closedAt: ticket?.closedAt
              ? DateTime?.fromISO(ticket.closedAt)?.toFormat(
                  "dd/MM/yyyy HH:mm:ss"
                )
              : null,
            duration: this.getDuration(ticket.closedAt, ticket.openedAt),
          }
        })
        .then(() => {
          this.getDepartmentListFromApi(this.customerData.siteId)
          this.getStaffListFromApi(this.customerData.siteId)
        })
        .catch(() => {
          this.toastError("Ticket Not Found")
          this.$router.push({ name: "internalTicketManagement" })
        })
    },

    async getThreadsFromApi() {
      this.internalThreadList = []
      await getThreadByTicketId(this.id, "INTERNAL").then((threads) => {
        threads.forEach((thread) => {
          this.internalThreadList.push({
            threadId: thread.threadId,
            name: thread.createdBy,
            detail: thread.threadMessage,
            datetime: DateTime.fromISO(thread.createdAt).toFormat(
              "dd/MM/yyyy HH:mm:ss"
            ),
            attachments: thread.attachments.map((attachment) => {
              return {
                id: attachment.attachmentId,
                name: attachment.fileName,
                originalFileName: attachment.originalFileName,
                path: attachment.attachmentPath,
                createdBy: attachment.createdBy,
              }
            }),
          })
        })
      })
    },
    async getActivitiesFromApi() {
      this.activityCommunityList = []
      await getThreadByTicketId(this.id, "GLOBAL").then((threads) => {
        threads.forEach((thread) => {
          this.activityCommunityList.push({
            threadId: thread.threadId,
            name: thread.createdBy,
            detail: thread.threadMessage,
            datetime: DateTime.fromISO(thread.createdAt).toFormat(
              "dd/MM/yyyy HH:mm:ss"
            ),
            attachments: thread.attachments.map((attachment) => {
              return {
                id: attachment.attachmentId,
                name: attachment.fileName,
                originalFileName: attachment.originalFileName,
                path: attachment.attachmentPath,
                createdBy: attachment.createdBy,
              }
            }),
          })
        })
      })
    },

    // on click ticket
    onClickDownloadFile(attachment) {
      window.open(attachment.path, attachment.fileName)
    },

    async onClickSaveEditTicket(ticket, uploadAttachment) {
      this.showLoading(true)
      await updateTicketById(this.id, ticket)
        .then(async (response) => {
          if (response.status === "ok") {
            this.toastSuccess("Ticket updated successfully")

            if (uploadAttachment) {
              await uploadTicketAttachments(this.id, uploadAttachment).then(
                (attachment) => {
                  if (attachment.status === "ok")
                    this.toastSuccess("Attachment uploaded successfully")
                  else this.toastError("Attachment uploaded failed")
                })
                .catch(() => {
                  this.toastError(
                    "Attachment uploaded failed. Please check the file size of your attachment and try again."
                  )
                })
            }
          } else this.toastError("Ticket updated failed")

          await this.getTicketDataFromAPI()
        })
        .catch((error) => this.toastError(error))
        .finally(() => this.showLoading(false))
    },

    // on click ticket detail
    async onClickAddTicketInternal(createInternal, attachment) {
      this.showLoading(true)
      await addThreadByTicketId(this.id, createInternal)
        .then(async (response) => {
          if (response.status === "ok") {
            this.toastSuccess("Thread added successfully")
            const thread = response.data

            if (attachment) {
              await uploadThreadAttachments(
                thread.threadId,
                this.id,
                attachment
              ).then((attachment) => {
                if (attachment.status === "ok")
                  this.toastSuccess("Attachment uploaded successfully")
                else this.toastError("Attachment uploaded failed")
              })
              .catch(() => {
                  this.toastError(
                    "Attachment uploaded failed. Please check the file size of your attachment and try again."
                  )
              })
            }

            if (createInternal.closeTicket)
              await this.$router.push({ name: "internalTicketManagement" })
            else await this.$router.go()
          } else this.toastError("Thread added failed")
        })
        .finally(() => this.showLoading(false))
    },

    async onClickAddTicketActivity(request, attachment) {
      this.showLoading(true)
      await addThreadByTicketId(this.id, request)
        .then(async (response) => {
          if (response.status === "ok") {
            this.toastSuccess("Activity added successfully")
            const thread = response.data

            if (attachment) {
              await uploadThreadAttachments(
                thread.threadId,
                this.id,
                attachment
              ).then((attachment) => {
                if (attachment.status === "ok")
                  this.toastSuccess("Attachment uploaded successfully")
                else this.toastError("Attachment uploaded failed")
              })
              .catch(() => {
                  this.toastError(
                    "Attachment uploaded failed. Please check the file size of your attachment and try again."
                  )
              })
            }

            if (request.closeTicket)
              await this.$router.push({ name: "internalTicketManagement" })
            else await this.$router.go()
          } else this.toastError("Activity added failed")
        })
        .finally(() => this.showLoading(false))
    },

    async getTicketEscalationActiveList() {
      this.showLoading(true)
      await getTicketEscalationActiveList()
        .then((response) => {
          this.escalationTemplateList = response.data
        })
        .finally(() => this.showLoading(false))
    },

    // get duration function
    getDuration(endDate, startDate) {
      if (!endDate) endDate = DateTime.now()

      // days
      const durationFromDays = parseInt(
        DateTime.fromISO(endDate)
          .diff(DateTime.fromISO(startDate), "days")
          .toObject().days
      )

      if (durationFromDays) return `${durationFromDays} days`

      // hours
      const durationFromHours = parseInt(
        DateTime.fromISO(endDate)
          .diff(DateTime.fromISO(startDate), "hours")
          .toObject().hours
      )

      if (durationFromHours) return `${durationFromHours} hours`

      // minutes
      const durationFromMinutes = parseInt(
        DateTime.fromISO(endDate)
          .diff(DateTime.fromISO(startDate), "minutes")
          .toObject().minutes
      )

      return `${durationFromMinutes} minutes`
    },

    onRequestDeleteAttachment(file) {
      const attachmentId = file.id
      this.sendRequestDeleteAttachmentToApi(this.id, attachmentId)
    },

    async sendRequestDeleteAttachmentToApi(ticketId, attachmentId) {
      this.showLoading(true)
      await deleteTicketAttachment(ticketId, attachmentId)
        .then(async (response) => {
          if (response.status === "ok") {
            this.toastSuccess("Attachment deleted successfully")
          } else this.toastError("Attachment delete failed")
          await this.getTicketDataFromAPI()
        })
        .finally(() => this.showLoading(false))
    },

    onRequestDeleteAttachmentFromThread(file, thread) {
      const attachmentId = file.id
      const threadId = thread.threadId
      this.sendRequestDeleteThreadAttachmentToApi(
        this.id,
        threadId,
        attachmentId
      )
    },

    async sendRequestDeleteThreadAttachmentToApi(
      ticketId,
      threadId,
      attachmentId
    ) {
      this.showLoading(true)
      await deleteThreadAttachment(ticketId, threadId, attachmentId)
        .then(async (response) => {
          if (response.status === "ok") {
            this.toastSuccess("Attachment deleted successfully")
          } else this.toastError("Attachment delete failed")
          await this.getTicketDataFromAPI()
        })
        .finally(() => this.showLoading(false))
    },

    async changeEscalationTemplate(escalationSelected) {
      this.escalation = escalationSelected
      this.escalationDetail = {
        name: "",
        description: "",
        levels: [],
      }

      if (this.escalation?.escalationId) {
        await this.getEscalationDetail(this.escalation?.escalationId)
      }
    },

    async getEscalationDetail(escalationId) {
      this.showLoading(true)
      await getTicketEscalationById(escalationId)
        .then((response) => {
          this.escalationDetail = response
        })
        .finally(() => this.showLoading(false))
    },

    async getTicketEscalationDetailByTicketId(ticketId) {
      this.showLoading(true)
      await getTicketEscalationByTicketId(ticketId)
        .then((response) => {
          this.escalationDetail = response
        })
        .finally(() => this.showLoading(false))
    },

    async onClickEscalateLevel(ticketId) {
      await escalateTicketEscalationByTicketId(ticketId).then((response) => {
        if (response.status === "ok") {
          this.toastSuccess("Escalate successfully")

          this.getTicketDataFromAPI()
        } else this.toastError("Escalate failed")
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.ticket-create-new-ticket__container {
  padding: 0 0 21px 0;
}
</style>
